/** Editing a batch's people: remove wholly or for a while, replace, add for all or part of the shift. */
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { addDays, format } from "date-fns";
import React, { useMemo } from "react";

import { HomeCrewMember } from "../../../../../../services/roster-assignments-api";
import { JobOutcomesState } from "../../controller/use-job-outcomes";
import { ManualMoves } from "../../controller/use-manual-moves";
import { CrewItem, fmtOvertime, isoAt, partHours, worksOnPart } from "../../utils/crew-board"; // eslint-disable-line sort-imports
import { jobLabel, LaneCard } from "../../utils/lane-rows"; // eslint-disable-line sort-imports
import { eyebrowSx, lbb } from "../../utils/tokens";
import { FillRequest } from "../fill-picker";
import { OutcomeSection } from "./outcome-section";

const actionSx: React.CSSProperties = {
	border: `1px solid ${lbb.navy20}`,
	background: lbb.paper,
	borderRadius: 4,
	padding: "3px 10px",
	fontFamily: lbb.fontUi,
	fontSize: 12,
	fontWeight: 600,
	color: lbb.navy,
	cursor: "pointer",
};

/**
 * The people on one shift's part of a batch, by crew line: take someone off
 * (the whole block or for a while — the Roster's own prompt), hand their block
 * to someone else, or add a person for the whole part or part of it (the fill
 * picker lets the window be narrowed).
 */
export const BatchEditor: React.FC<{
	item: CrewItem;
	workstation: string;
	weekStart: Date;
	crews: HomeCrewMember[];
	moves: ManualMoves;
	onFill: (request: FillRequest) => void;
	onClose: () => void;
	outcomes: JobOutcomesState;
	onUpdateSchedule: (workOrderId: number) => void;
	onUndoUpdate: (moveBatchId: string) => void;
}> = ({ item, workstation, weekStart, crews, moves, onFill, onClose, outcomes, onUpdateSchedule, onUndoUpdate }) => {
	const { step } = item;
	const part = { start: item.start, end: item.end };
	const crewOf = useMemo(() => new Map(crews.map((m) => [m.workerId, m])), [crews]);
	const groups = step.groups.filter((g) => g.dropPhaseId !== null && g.start < part.end && g.end > part.start);
	const peopleIn = (cards: LaneCard[]) => {
		const seen = new Set<string>();
		return cards
			.filter((c) => worksOnPart(c, part))
			.filter((c) => (seen.has(c.workerId) ? false : (seen.add(c.workerId), true)))
			.sort((a, b) => a.start - b.start);
	};
	const startIso = isoAt(weekStart, part.start);
	const endIso = isoAt(weekStart, part.end);
	const handleRemove = (id: number) => {
		onClose();
		moves.openRemove(id);
	};
	const handleReplace = (id: number) => {
		onClose();
		moves.replace(id);
	};
	const handleAdd = (groupIndex: number) => {
		const group = groups[groupIndex];
		onClose();
		onFill({
			workOrderId: step.workOrderId,
			phaseId: group.dropPhaseId,
			start: startIso,
			end: endIso,
			jobRoleId: group.jobRoleId,
			jobRoleName: group.jobRole,
			title: `${jobLabel(step)} · ${group.operationName} · ${group.jobRole}`,
		});
	};
	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy, pb: 0.5 }}>
				{jobLabel(step)}
				<div style={{ fontFamily: lbb.fontUi, fontSize: 13, fontWeight: 400, color: lbb.ink60, marginTop: 2 }}>
					{step.subtitle ? `${step.subtitle} · ` : ""}{workstation} · {format(addDays(weekStart, Math.floor(part.start / 24)), "EEE d MMM")} {partHours(part.start, part.end)}
					{item.overtimeHours > 0 ? ` · ${fmtOvertime(item.overtimeHours)}` : ""}
				</div>
			</DialogTitle>
			<DialogContent>
				{groups.length === 0 && (
					<p style={{ fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink60 }}>This batch has no crew lines on its standard, so people cannot be placed on it here.</p>
				)}
				{groups.map((group, g) => {
					const people = peopleIn(group.cards);
					return (
						<div key={group.key} style={{ borderTop: g ? lbb.borderHair : undefined, paddingTop: g ? 12 : 4, marginTop: g ? 12 : 0 }}>
							<div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
								<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>{group.operationName}</span>
								<span style={{ fontFamily: lbb.fontUi, fontSize: 13, fontWeight: 600, color: lbb.navy }}>{group.jobRole}</span>
								<span style={{ marginLeft: "auto", fontFamily: lbb.fontMono, fontSize: 12, color: people.length >= group.needed ? lbb.success : lbb.brick }}>
									{people.length}/{group.needed}
								</span>
							</div>
							{people.map((card) => {
								const home = crewOf.get(card.workerId);
								const base = home ? `${home.workstation}${home.shiftLabel ? ` · ${home.shiftLabel}` : ""}` : "another plant or line";
								return (
									<div key={card.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: lbb.borderHair }}>
										<div style={{ minWidth: 0, flex: 1 }}>
											<div style={{ fontFamily: lbb.fontUi, fontSize: 14, fontWeight: 600, color: lbb.navy }}>{card.workerName}</div>
											<div style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.ink60 }}>
												{partHours(Math.max(card.start, part.start), Math.min(card.end, part.end))} · base {base}
												{card.isManuallyAssigned ? " · pinned" : ""}
												{card.isOut ? " · out" : ""}
											</div>
										</div>
										<button type="button" style={actionSx} onClick={() => handleReplace(card.id)}>
											Replace…
										</button>
										<button type="button" style={{ ...actionSx, color: lbb.brick, borderColor: lbb.brick }} onClick={() => handleRemove(card.id)}>
											Remove…
										</button>
									</div>
								);
							})}
							{people.length === 0 && <div style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.navy40, padding: "4px 0" }}>No one yet.</div>}
							<button type="button" style={{ ...actionSx, marginTop: 8, borderStyle: "dashed" }} onClick={() => handleAdd(g)}>
								+ Add a person (all of {partHours(part.start, part.end)} or part of it)
							</button>
						</div>
					);
				})}
				<p style={{ fontFamily: lbb.fontUi, fontSize: 11.5, color: lbb.ink60, marginTop: 14, marginBottom: 0 }}>
					Remove asks whether to take the person off the whole block or for a while. Add opens the bench; narrow the hours there to add someone for part of the shift.
				</p>
				<OutcomeSection
					workOrderId={step.workOrderId}
					outcome={outcomes.index.byOrder.get(step.workOrderId)}
					started={new Date(isoAt(weekStart, step.start)).getTime() <= Date.now()}
					onRecord={outcomes.record}
					onUpdateSchedule={onUpdateSchedule}
					onUndo={onUndoUpdate}
				/>
			</DialogContent>
			<DialogActions>
				<button type="button" style={actionSx} onClick={onClose}>
					Done
				</button>
			</DialogActions>
		</Dialog>
	);
};
